import { useState } from "react";
import api from "@/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "lucide-react";

const WARD_TYPES = [
  { value: "general", label: "General" },
  { value: "icu", label: "ICU" },
  { value: "maternity", label: "Maternity" },
  { value: "pediatric", label: "Pediatric" },
  { value: "isolation", label: "Isolation" },
];

export default function NewWardModal({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ ward_name: "", ward_type: "general", total_beds: "", charges_per_bed_per_day: "" });

  const submit = async () => {
    if (!f.ward_name || !f.total_beds || !f.charges_per_bed_per_day) {
      toast.error("Fill all fields"); return;
    }
    setSaving(true);
    try {
      await api.post("/ipd/wards", {
        ward_name: f.ward_name, ward_type: f.ward_type,
        total_beds: Number(f.total_beds),
        charges_per_bed_per_day: Number(f.charges_per_bed_per_day),
      });
      toast.success("Ward created");
      setOpen(false); setF({ ward_name: "", ward_type: "general", total_beds: "", charges_per_bed_per_day: "" });
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="ipd-new-ward-btn">
          <Plus size={14} className="mr-1" /> New Ward
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="ipd-new-ward-modal">
        <DialogHeader><DialogTitle>New Ward</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div><Label>Ward name *</Label>
            <Input value={f.ward_name} onChange={(e) => setF({ ...f, ward_name: e.target.value })}
                   data-testid="ipd-ward-name" /></div>
          <div><Label>Ward type *</Label>
            <Select value={f.ward_type} onValueChange={(v) => setF({ ...f, ward_type: v })}>
              <SelectTrigger data-testid="ipd-ward-type"><SelectValue /></SelectTrigger>
              <SelectContent>{WARD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Total beds *</Label>
            <Input type="number" value={f.total_beds} onChange={(e) => setF({ ...f, total_beds: e.target.value })}
                   data-testid="ipd-ward-total-beds" /></div>
          <div><Label>Charges per bed per day (₹) *</Label>
            <Input type="number" value={f.charges_per_bed_per_day}
                   onChange={(e) => setF({ ...f, charges_per_bed_per_day: e.target.value })}
                   data-testid="ipd-ward-charges" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-sky-500 hover:bg-sky-600 text-white"
                  data-testid="ipd-ward-submit">{saving ? "Saving…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
